import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getDefaultStore } from 'jotai';
import { currentLangAtom } from '@/store/langAtom';
// en.json is the only i18next bundle — short UI-chrome strings (nav labels, button text,
// ARIA strings) that must be synchronous and survive a Content API outage.
// All user-facing verbiage goes through content-defaults.json + paragraph config API.
import en from '@/locales/en.json';

export const LANGUAGES = [
  { code: 'en', label: 'English',  dir: 'ltr' },
  { code: 'es', label: 'Español',  dir: 'ltr' },
  { code: 'fr', label: 'Français', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
] as const;

export type LanguageCode = typeof LANGUAGES[number]['code'];

const COOKIE_KEY     = 'pp_lang';
const STORAGE_KEY    = 'lang';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year in seconds

/* ── Cookie helpers ── */
function readCookie(name: string): string | null {
  // String scan — avoids new RegExp(variable) on cookie names.
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    if (trimmed.slice(0, eq) === name) {
      return decodeURIComponent(trimmed.slice(eq + 1));
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAge: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`;
}

/* ── Browser language detection ── */
export function detectBrowserLanguage(): LanguageCode {
  const candidates = Array.from(navigator.languages ?? [navigator.language]);
  for (const lang of candidates) {
    const code = lang.split('-')[0]!.toLowerCase();
    if (LANGUAGES.some(l => l.code === code)) return code as LanguageCode;
  }
  return 'en';
}

function isKnownBundleCode(code: string | null): code is LanguageCode {
  return LANGUAGES.some(l => l.code === code);
}

/* ── Resolve initial language (priority: localStorage → cookie → browser → 'en') ── */
function resolveInitialLanguage(): string {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved) return saved;
  const cookie = readCookie(COOKIE_KEY);
  if (cookie) return cookie;
  return detectBrowserLanguage();
}

/* ── Resolve i18next bundle code from a full ISO code ── */
// Full ISO code (e.g. 'en-AU') → bundle prefix ('en') when no exact bundle exists.
function resolveBundleCode(code: string): LanguageCode {
  if (isKnownBundleCode(code)) return code;
  const prefix = code.split('-')[0]!.toLowerCase();
  if (isKnownBundleCode(prefix)) return prefix as LanguageCode;
  return 'en';
}

/* ── Apply language (call this on every language switch) ── */
// Accepts any app_language_code string from l_app_language — 'en', 'en-AU', 'hr', etc.
// Writing to currentLangAtom is the primary reactive update; all useAtomValue(currentLangAtom)
// subscribers re-render automatically without needing an i18next event listener.
// localStorage write is kept so the Axios interceptor and other non-React code stay in sync.
export function applyLanguage(code: string): void {
  localStorage.setItem(STORAGE_KEY, code);
  writeCookie(COOKIE_KEY, code, COOKIE_MAX_AGE);

  getDefaultStore().set(currentLangAtom, code);

  void i18n.changeLanguage(resolveBundleCode(code));

  document.documentElement.lang = code;
  document.documentElement.dir = code.startsWith('ar') ? 'rtl' : 'ltr';
}

/* ── Initialise ── */
const initialCode = resolveInitialLanguage();
const initialBundleCode = resolveBundleCode(initialCode);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
  },
  lng: initialBundleCode,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

// Sync <html lang> and <html dir> on startup
document.documentElement.lang = initialCode;
document.documentElement.dir = initialCode.startsWith('ar') ? 'rtl' : 'ltr';

export default i18n;
